import {Component, NgZone, ChangeDetectionStrategy} from "@angular/core";
import {NzMessageService} from "ng-zorro-antd/message";
import {APCheckLogSearchDto, APCheckPrintLogDto, PerFormaAPIService} from "src/app/services/pfapi.service";
import {lastValueFrom} from "rxjs";
import {NzTableQueryParams, NzTableSortOrder} from "ng-zorro-antd/table";
import Utils from "src/utils";

export interface SortDto {
    key: string;
    value: NzTableSortOrder;
}

export enum EntryType {
    User,
    Application
}

@Component({
    selector: "app-ap-log",
    templateUrl: "./ap-log.component.html",
    styleUrl: "./ap-log.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ApLogComponent {
    constructor(
        private readonly api: PerFormaAPIService,
        private message: NzMessageService,
        private zone: NgZone
    ) {}

    logs: APCheckPrintLogDto[] = [];
    loading = true;
    total = 0;
    pageSizeOptions = [10, 15, 20, 50];
    pageSize = 15;
    pageIndex = 1;

    private lastParams: NzTableQueryParams | null = null;

    // options for the time pickers
    dtpBeginOptions = {
        nzFormat: "h:mm:ss a",
        nzUse12Hours: true,
        nzDefaultOpenValue: this.getDefaultStartTime()
    };

    dtpEndOptions = {
        nzFormat: "h:mm:ss a",
        nzUse12Hours: true,
        nzDefaultOpenValue: this.getDefaultEndTime()
    };

    // start out with a default sort order so
    // that the column header shows the correct state
    defaultSort: SortDto[] = [
        {key: "LogDate", value: "descend"},
        {key: "User", value: null}
    ];

    sort: SortDto[] = [...this.defaultSort];

    logType = "all";
    searchField = "message";
    searchValue = "";
    startDate: Date | null = null;
    endDate: Date | null = null;

    searchFields = [
        {label: "Message", value: "message"},
        {label: "User", value: "user"}
    ];

    columns = [
        {
            title: "Log Date",
            compare: (a: APCheckPrintLogDto, b: APCheckPrintLogDto) =>
                new Date(a.LogDate).getTime() - new Date(b.LogDate).getTime(),
            fieldName: "LogDate",
            width: "175px"
        },
        {
            title: "User",
            compare: (a: APCheckPrintLogDto, b: APCheckPrintLogDto) => a.UserName.localeCompare(b.UserName),
            fieldName: "User"
        },
        {
            title: "Entry Type",
            compare: null,
            fieldName: null,
            width: "100px"
        },
        {
            title: "Message",
            compare: null,
            fieldName: null
        },
        {
            title: "Info",
            compare: null,
            fieldName: null
        }
    ];

    GetData = async () => {
        this.loading = true;

        const params = this.getSearchParams();

        try {
            const result = await lastValueFrom(this.api.postApiAPCheckSearchLogs(params));
            this.logs = result.Data;
            this.total = result.Total;
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                this.message.error(errors[0]);
            }
        } finally {
            this.loading = false;
        }
    };

    ExportData = async () => {
        this.loading = true;

        const params = this.getSearchParams();

        try {
            const blob = await lastValueFrom(
                this.api.postApiAPCheckExportLogs(params, {
                    observe: "response"
                })
            );

            Utils.downloadFileContent(blob, true);
        } catch (e) {
            console.log(e);
            this.message.error("something went wrong");
        } finally {
            this.loading = false;
        }
    };

    getSearchParams = (): APCheckLogSearchDto => {
        const params: APCheckLogSearchDto = {
            PageIndex: this.pageIndex,
            PageSize: this.pageSize,
            SearchField: this.searchField || null,
            SearchValue: this.searchValue || null,
            LogType: this.logType != "all" ? Number(this.logType) : null,
            StartDate: this.startDate ? this.startDate.toISOString() : null,
            EndDate: this.endDate ? this.endDate.toISOString() : null
        };

        const sort = this.sort?.filter(s => s.value !== null) || [...this.defaultSort];

        if (sort.length) {
            params.SortField = sort[0].key;
            params.SortOrder = sort[0].value;
        }

        return params;
    };

    async onQueryParamsChange(params: NzTableQueryParams): Promise<void> {
        if (this.isParamsSame(this.lastParams, params)) {
            return;
        }

        this.lastParams = {...params};

        this.pageIndex = params.pageIndex;
        this.pageSize = params.pageSize;

        // reset the page index if the sort changes
        if (this.SortChanged(params.sort)) {
            this.pageIndex = 1;
        }

        this.sort = [...params.sort];
        await this.GetData();
    }

    // nz-table likes to fire extraneous onQueryParamsChange events
    // so we need to filter out the ones that don't actually change anything
    private isParamsSame(a: NzTableQueryParams | null, b: NzTableQueryParams): boolean {
        if (!a) return false;
        const activeSortA = a.sort.filter(s => s.value !== null);
        const activeSortB = b.sort.filter(s => s.value !== null);
        return (
            a.pageIndex === b.pageIndex &&
            a.pageSize === b.pageSize &&
            activeSortA.length === activeSortB.length &&
            activeSortA.every((s, i) => s.key === activeSortB[i].key && s.value === activeSortB[i].value)
        );
    }

    SortChanged = (sort: SortDto[]): boolean => {
        // Filter out non-active sorts (where value is null)
        const newSort = sort.filter(s => s.value !== null);
        const currentSort = this.sort
            ? this.sort.filter(s => s.value !== null)
            : [...this.defaultSort].filter(s => s.value !== null);

        // If both are empty, no sort change
        if (newSort.length === 0 && currentSort.length === 0) {
            return false;
        }

        // If lengths differ, there's a change (e.g., one has a sort, the other doesn't)
        if (newSort.length !== currentSort.length) {
            return true;
        }

        const newActiveSort = newSort[0];
        const currentActiveSort = currentSort[0];

        return newActiveSort.key !== currentActiveSort.key || newActiveSort.value !== currentActiveSort.value;
    };

    getSortOrder(fieldName: string | null): NzTableSortOrder {
        if (!fieldName || !this.sort) {
            return null;
        }
        const sortItem = this.sort.find(s => s.key === fieldName);
        return sortItem ? sortItem.value : null;
    }

    getTypeName(typeId: number) {
        return EntryType[typeId];
    }

    getDefaultStartTime() {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }

    getDefaultEndTime() {
        const d = new Date();
        d.setHours(23, 59, 59, 997);
        return d;
    }

    // Handle search button click
    async onSearch(): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    // Clear search
    async clearSearch(): Promise<void> {
        this.searchField = "message";
        this.searchValue = "";
        this.startDate = null;
        this.endDate = null;
        this.logType = "all";
        this.pageIndex = 1;
        this.sort = [...this.defaultSort];
        await this.GetData();
    }

    // Handle search button click
    async onExport(): Promise<void> {
        await this.ExportData();
    }

    // Handle log type selection
    async onLogTypeChange($event): Promise<void> {
        this.pageIndex = 1;
        await this.GetData();
    }

    async onStartDateChange($event): Promise<void> {
        //if (!$event) this.startTime = null;

        this.pageIndex = 1;
        await this.GetData();
    }

    async onEndDateChange($event): Promise<void> {
        //if (!$event) this.endTime = null;

        this.pageIndex = 1;
        await this.GetData();
    }
}
